import jsPDF from 'jspdf';
import { Invoice } from './paymentService';
import { PDFTemplateService } from './pdfTemplateService';

export interface CompanyInfo {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  website?: string;
  logo?: string;
}

export interface PDFLayoutOptions {
  layout: 'professional' | 'compact';
  includeCompanyLogo: boolean;
  showInternalNotes: boolean;
  showPartDetails: boolean;
  showDiscountBreakdown: boolean;
  highlightDiscounts: boolean;
  showOriginalPrices: boolean;
  colorScheme: 'blue' | 'green' | 'gray' | 'red' | 'purple' | 'orange';
  fontFamily: 'helvetica' | 'times' | 'courier';
  fontSize: 'small' | 'medium' | 'large';
  spacing: 'tight' | 'normal' | 'loose';
  borderStyle: 'none' | 'simple' | 'rounded';
  showPageNumbers: boolean;
}

export class InvoicePDFService {
  private static readonly DEFAULT_COMPANY_INFO: CompanyInfo = {
    name: 'CNF Auto LLC',
    address: '123 Main Street',
    city: 'City',
    state: 'State',
    zip: '12345',
    phone: '(555) 123-4567',
    email: 'info@cnfautosuite.com',
    website: 'www.cnfautosuite.com',
  };

  private static formatCurrency(amount: number): string {
    return `$${(Math.round(amount * 100) / 100).toFixed(2)}`;
  }

  private static formatPercentage(percent: number): string {
    return `${(Math.round(percent * 100) / 100).toFixed(2)}%`;
  }

  private static checkPageBreak(pdf: jsPDF, yPosition: number, requiredSpace: number): number {
    const pageHeight = pdf.internal.pageSize.getHeight();
    const footerSpace = 30;

    if (yPosition + requiredSpace + footerSpace > pageHeight) {
      pdf.addPage();
      return 30;
    }
    return yPosition;
  }

  private static readonly COLOR_SCHEMES = {
    blue: { primary: '#1890ff', secondary: '#f0f8ff', text: '#262626' },
    green: { primary: '#22c55e', secondary: '#f0fdf4', text: '#262626' },
    gray: { primary: '#595959', secondary: '#fafafa', text: '#262626' },
    red: { primary: '#ff4d4f', secondary: '#fff2f0', text: '#262626' },
    purple: { primary: '#722ed1', secondary: '#f9f0ff', text: '#262626' },
    orange: { primary: '#fa8c16', secondary: '#fff7e6', text: '#262626' },
  };

  private static readonly FONT_SETTINGS = {
    small: { title: 20, header: 12, body: 9, footer: 7 },
    medium: { title: 24, header: 14, body: 10, footer: 8 },
    large: { title: 28, header: 16, body: 12, footer: 9 },
  };

  private static readonly SPACING_SETTINGS = {
    tight: { lineHeight: 1.2, sectionGap: 8, itemGap: 5 },
    normal: { lineHeight: 1.4, sectionGap: 12, itemGap: 8 },
    loose: { lineHeight: 1.6, sectionGap: 16, itemGap: 10 },
  };

  static async generateInvoicePDF(
    invoice: Invoice,
    options?: Partial<PDFLayoutOptions>,
    companyInfo?: Partial<CompanyInfo>
  ): Promise<void> {
    const defaultOptions = { colorScheme: 'green' as const, showPartDetails: true, showDiscountBreakdown: true };
    const finalOptions = PDFTemplateService.getSettingsWithOverrides({ ...defaultOptions, ...options });
    const finalCompanyInfo = PDFTemplateService.getCompanyInfoWithOverrides(companyInfo);
    const pdf = await this.createPDF(invoice, finalOptions, finalCompanyInfo);

    pdf.save(`invoice-${invoice.invoice_number}.pdf`);
  }

  static async generateInvoicePDFBuffer(
    invoice: Invoice,
    options?: Partial<PDFLayoutOptions>,
    companyInfo?: Partial<CompanyInfo>
  ): Promise<string> {
    const defaultOptions = { colorScheme: 'green' as const, showPartDetails: true, showDiscountBreakdown: true };
    const finalOptions = PDFTemplateService.getSettingsWithOverrides({ ...defaultOptions, ...options });
    const finalCompanyInfo = PDFTemplateService.getCompanyInfoWithOverrides(companyInfo);
    const pdf = await this.createPDF(invoice, finalOptions, finalCompanyInfo);

    return pdf.output('datauristring').split(',')[1];
  }

  private static async createPDF(
    invoice: Invoice,
    options: PDFLayoutOptions,
    companyInfo: CompanyInfo
  ): Promise<jsPDF> {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const colors = this.COLOR_SCHEMES[options.colorScheme];
    const fonts = this.FONT_SETTINGS[options.fontSize];
    const spacing = this.SPACING_SETTINGS[options.spacing];

    pdf.setFont(options.fontFamily, 'normal');

    await this.generateProfessionalLayout(pdf, invoice, options, companyInfo, colors);

    return pdf;
  }

  private static async generateProfessionalLayout(
    pdf: jsPDF,
    invoice: Invoice,
    options: PDFLayoutOptions,
    companyInfo: CompanyInfo,
    colors: any
  ): Promise<void> {
    const fonts = this.FONT_SETTINGS[options.fontSize];
    const spacing = this.SPACING_SETTINGS[options.spacing];
    const pageWidth = pdf.internal.pageSize.getWidth();
    let yPosition = 20;

    // Header
    yPosition = this.addHeader(pdf, companyInfo, options.includeCompanyLogo, colors, yPosition);

    // Title
    pdf.setFontSize(fonts.title);
    pdf.setTextColor(colors.primary);
    pdf.text('INVOICE', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += spacing.sectionGap + 7;

    // PAID badge
    yPosition = this.addPaidBadge(pdf, invoice, colors, yPosition, pageWidth, fonts);

    // Invoice details
    yPosition = this.addInvoiceDetails(pdf, invoice, colors, yPosition, fonts, pageWidth);

    // Customer and vehicle info
    yPosition = this.addCustomerVehicleInfo(pdf, invoice, colors, yPosition, pageWidth, fonts);

    // Line items table
    yPosition = this.addLineItemsTable(pdf, invoice, options, colors, yPosition, pageWidth, fonts);

    // Total section
    yPosition = this.addTotalSection(pdf, invoice, colors, yPosition, pageWidth);

    // Notes
    if (invoice.notes) {
      yPosition = this.addNotes(pdf, invoice, colors, yPosition, pageWidth);
    }

    // Customer Signature (if available from estimate/WO approval)
    if ((invoice as any).signature_data) {
      yPosition = this.addSignatureSection(pdf, invoice, colors, yPosition, pageWidth);
    }

    // Footer
    this.addFooter(pdf, companyInfo, colors, options.showPageNumbers);
  }

  private static addHeader(
    pdf: jsPDF,
    companyInfo: CompanyInfo,
    includeLogo: boolean,
    colors: any,
    yPosition: number
  ): number {
    const pageWidth = pdf.internal.pageSize.getWidth();

    pdf.setFontSize(20);
    pdf.setTextColor(colors.primary);
    pdf.text(companyInfo.name, 20, yPosition);

    pdf.setFontSize(10);
    pdf.setTextColor(colors.text);
    yPosition += 8;
    pdf.text(companyInfo.address, 20, yPosition);
    yPosition += 4;
    pdf.text(`${companyInfo.city}, ${companyInfo.state} ${companyInfo.zip}`, 20, yPosition);
    yPosition += 4;
    pdf.text(`Phone: ${companyInfo.phone}`, 20, yPosition);
    yPosition += 4;
    pdf.text(`Email: ${companyInfo.email}`, 20, yPosition);

    if (companyInfo.website) {
      yPosition += 4;
      pdf.text(`Web: ${companyInfo.website}`, 20, yPosition);
    }

    if (includeLogo) {
      if (companyInfo.logo) {
        try {
          pdf.addImage(companyInfo.logo, 'JPEG', pageWidth - 60, 20, 40, 30);
        } catch (error) {
          pdf.setDrawColor(colors.primary);
          pdf.rect(pageWidth - 60, 20, 40, 30);
          pdf.setFontSize(8);
          pdf.text('LOGO', pageWidth - 40, 37, { align: 'center' });
        }
      } else {
        pdf.setDrawColor(colors.primary);
        pdf.rect(pageWidth - 60, 20, 40, 30);
        pdf.setFontSize(8);
        pdf.text('LOGO', pageWidth - 40, 37, { align: 'center' });
      }
    }

    return yPosition + 15;
  }

  private static addPaidBadge(
    pdf: jsPDF,
    invoice: Invoice,
    colors: any,
    yPosition: number,
    pageWidth: number,
    fonts: any
  ): number {
    const badgeWidth = 90;  // Increased from 70 to accommodate text
    const badgeHeight = 16;
    const badgeX = (pageWidth - badgeWidth) / 2;

    pdf.setFillColor(34, 197, 94);
    pdf.rect(badgeX, yPosition, badgeWidth, badgeHeight, 'F');

    pdf.setDrawColor(22, 163, 74);
    pdf.setLineWidth(1);
    pdf.rect(badgeX, yPosition, badgeWidth, badgeHeight);

    pdf.setFontSize(13);  // Slightly reduced from 14
    pdf.setTextColor(255, 255, 255);
    pdf.setFont(undefined, 'bold');
    pdf.text('PAID IN FULL', pageWidth / 2, yPosition + 11, { align: 'center' });
    pdf.setFont(undefined, 'normal');

    return yPosition + badgeHeight + 12;
  }

  private static addInvoiceDetails(
    pdf: jsPDF,
    invoice: Invoice,
    colors: any,
    yPosition: number,
    fonts: any,
    pageWidth: number
  ): number {
    // Calculate box height based on number of payments
    const hasMultiplePayments = invoice.payments && invoice.payments.length > 1;
    const paymentCount = hasMultiplePayments ? invoice.payments!.length : 1;
    const baseHeight = 32;
    const additionalHeight = hasMultiplePayments ? (paymentCount - 1) * 5 : 0;
    const boxHeight = baseHeight + additionalHeight;

    pdf.setDrawColor(colors.primary);
    pdf.setFillColor(colors.secondary);
    pdf.rect(20, yPosition, pageWidth - 40, boxHeight, 'F');
    pdf.rect(20, yPosition, pageWidth - 40, boxHeight);

    pdf.setFontSize(10);  // Reduced from fonts.header (14) to 10 for better fit
    pdf.setTextColor(colors.text);

    // Left column with better spacing
    const leftLabelX = 25;
    const leftValueX = 63;  // Increased from 55 to 63 for more space

    pdf.text('Invoice #:', leftLabelX, yPosition + 9);
    pdf.setFont(undefined, 'bold');
    pdf.text(invoice.invoice_number, leftValueX, yPosition + 9);

    pdf.setFont(undefined, 'normal');
    pdf.text('Invoice Date:', leftLabelX, yPosition + 17);
    pdf.text(new Date(invoice.invoice_date).toLocaleDateString(), leftValueX, yPosition + 17);

    pdf.text('Payment Date:', leftLabelX, yPosition + 25);
    pdf.text(invoice.payment_date ? new Date(invoice.payment_date).toLocaleDateString() : 'N/A', leftValueX, yPosition + 25);

    // Right column with better spacing
    const rightLabelX = pageWidth / 2 + 15;  // Start right column at middle of page
    const rightValueX = pageWidth / 2 + 55;  // More space for values

    pdf.text('Status:', rightLabelX, yPosition + 9);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(34, 197, 94);
    pdf.text('PAID', rightValueX, yPosition + 9);

    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(colors.text);

    // Payment method(s) - show all payments if multiple, otherwise show single payment method
    if (hasMultiplePayments) {
      pdf.text('Payment Methods:', rightLabelX, yPosition + 17);
      let paymentY = yPosition + 22;

      const paymentMethodMap: { [key: string]: string } = {
        'card': 'Card',
        'cash': 'Cash',
        'check': 'Check',
        'affirm': 'Affirm',
        'paypal': 'PayPal',
        'other': 'Other'
      };

      invoice.payments!.forEach((payment, index) => {
        pdf.setFontSize(9);

        // Format payment method display
        let methodDisplay = '';
        const methodName = paymentMethodMap[payment.payment_method] || payment.payment_method;

        if (payment.card_brand && payment.card_last4) {
          // Card payment with details: "Visa ****1234"
          const brandCapitalized = payment.card_brand.charAt(0).toUpperCase() + payment.card_brand.slice(1);
          methodDisplay = `${brandCapitalized} ****${payment.card_last4}`;
        } else if (payment.payment_method === 'affirm') {
          methodDisplay = 'Affirm';
        } else {
          methodDisplay = methodName;
        }

        // Display payment method and amount
        const paymentText = `${methodDisplay}: ${this.formatCurrency(payment.amount)}`;
        pdf.text(paymentText, rightValueX, paymentY);
        paymentY += 5;
      });

      pdf.setFontSize(10);
    } else {
      pdf.text('Payment Method:', rightLabelX, yPosition + 17);

      const paymentMethodMap: { [key: string]: string } = {
        'card': 'Card',
        'cash': 'Cash',
        'check': 'Check',
        'affirm': 'Affirm',
        'paypal': 'PayPal',
        'other': 'Other'
      };

      const paymentMethod = invoice.payment_method ?
        (paymentMethodMap[invoice.payment_method] || invoice.payment_method) : 'N/A';
      pdf.text(paymentMethod, rightValueX, yPosition + 17);
    }

    return yPosition + boxHeight + 10;  // Return position after box + spacing
  }

  private static addCustomerVehicleInfo(
    pdf: jsPDF,
    invoice: Invoice,
    colors: any,
    yPosition: number,
    pageWidth: number,
    fonts: any
  ): number {
    const startYPosition = yPosition;

    // Customer info
    pdf.setFontSize(fonts.header);
    pdf.setTextColor(colors.primary);
    pdf.text('CUSTOMER', 20, yPosition);

    pdf.setFontSize(fonts.body);
    pdf.setTextColor(colors.text);
    yPosition += 8;

    if (invoice.customer) {
      const customerName = invoice.customer.company_name ||
        `${invoice.customer.first_name} ${invoice.customer.last_name}`;
      pdf.text(customerName, 20, yPosition);
      yPosition += 5;

      if (invoice.customer.address) {
        pdf.text(invoice.customer.address, 20, yPosition);
        yPosition += 5;
        pdf.text(`${invoice.customer.city}, ${invoice.customer.state} ${invoice.customer.zip}`, 20, yPosition);
        yPosition += 5;
      }

      pdf.text(`Phone: ${invoice.customer.phone}`, 20, yPosition);
      yPosition += 5;
      pdf.text(`Email: ${invoice.customer.email}`, 20, yPosition);
    }

    // Vehicle info
    let vehicleYPosition = startYPosition;

    pdf.setFontSize(fonts.header);
    pdf.setTextColor(colors.primary);
    pdf.text('VEHICLE', pageWidth / 2 + 10, vehicleYPosition);

    pdf.setFontSize(fonts.body);
    pdf.setTextColor(colors.text);
    vehicleYPosition += 8;

    if (invoice.vehicle) {
      pdf.text(`${invoice.vehicle.year} ${invoice.vehicle.make} ${invoice.vehicle.model}`,
        pageWidth / 2 + 10, vehicleYPosition);
      vehicleYPosition += 5;

      pdf.text(`VIN: ${invoice.vehicle.vin}`, pageWidth / 2 + 10, vehicleYPosition);
      vehicleYPosition += 5;

      if (invoice.vehicle.license_plate) {
        pdf.text(`License: ${invoice.vehicle.license_plate}`, pageWidth / 2 + 10, vehicleYPosition);
        vehicleYPosition += 5;
      }

      if (invoice.vehicle.current_mileage) {
        pdf.text(`Mileage: ${invoice.vehicle.current_mileage.toLocaleString()}`,
          pageWidth / 2 + 10, vehicleYPosition);
        vehicleYPosition += 5;
      }
    }

    return Math.max(yPosition, vehicleYPosition) + 10;
  }

  private static addLineItemsTable(
    pdf: jsPDF,
    invoice: Invoice,
    options: PDFLayoutOptions,
    colors: any,
    yPosition: number,
    pageWidth: number,
    fonts: any
  ): number {
    pdf.setFontSize(fonts.header);
    pdf.setTextColor(colors.primary);
    pdf.text('SERVICES & PARTS', 20, yPosition);
    yPosition += 15;

    if (!invoice.line_items || invoice.line_items.length === 0) {
      pdf.setFontSize(fonts.body);
      pdf.setTextColor(colors.text);
      pdf.text('No line items', 20, yPosition);
      return yPosition + 10;
    }

    // Calculate if we have any discounts or waivers
    const hasDiscounts = invoice.line_items.some(item =>
      (item.labor_discount_amount && item.labor_discount_amount > 0) || item.is_diagnosis_waived
    );

    // Table structure
    const tableHeaders = ['Description', 'Qty', 'Rate/Price', 'Amount'];
    const colWidths = [90, 20, 40, 30];
    const tableWidth = colWidths.reduce((sum, width) => sum + width, 0);
    const startX = 20;

    // Table header
    pdf.setFillColor(colors.primary);
    pdf.rect(startX, yPosition, tableWidth, 10, 'F');

    if (options.borderStyle !== 'none') {
      pdf.setDrawColor(colors.primary);
      pdf.setLineWidth(0.5);
      pdf.rect(startX, yPosition, tableWidth, 10);
    }

    pdf.setFontSize(fonts.body);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont(undefined, 'bold');

    let xPos = startX + 3;
    tableHeaders.forEach((header, index) => {
      const textAlign = index === 0 ? 'left' : index === tableHeaders.length - 1 ? 'right' : 'center';
      const textX = textAlign === 'right' ? xPos + colWidths[index] - 3 :
        textAlign === 'center' ? xPos + colWidths[index] / 2 : xPos;

      pdf.text(header, textX, yPosition + 7, { align: textAlign });
      xPos += colWidths[index];
    });

    yPosition += 10;
    pdf.setFont(undefined, 'normal');

    // Table rows
    invoice.line_items.forEach((item, index) => {
      // Calculate space needed for page break - estimate conservatively
      let estimatedSpace = 25; // Base space for one-line concern

      // Add space for cause and correction
      if (item.cause) estimatedSpace += 15; // ~4 lines max
      if (item.correction) estimatedSpace += 15; // ~4 lines max

      // Add space for parts breakdown table
      if (item.parts && item.parts.length > 0) {
        estimatedSpace += 10 + (item.parts.length * 7);
      }

      yPosition = this.checkPageBreak(pdf, yPosition, estimatedSpace);

      // Dynamic row height - calculate based on description AND rate/price content
      const descWidth = colWidths[0] - 6;

      // Calculate actual line count for concern with full wrapping
      const concernLines = pdf.splitTextToSize(`Concern: ${item.concern}`, descWidth);
      let descLineCount = concernLines.length;

      // Calculate actual line count for cause with full wrapping
      if (item.cause) {
        const causeLines = pdf.splitTextToSize(`Cause: ${item.cause}`, descWidth);
        descLineCount += causeLines.length + 0.3; // +0.3 for small gap
      }

      // Calculate actual line count for correction with full wrapping
      if (item.correction) {
        const correctionLines = pdf.splitTextToSize(`Correction: ${item.correction}`, descWidth);
        descLineCount += correctionLines.length + 0.3; // +0.3 for small gap
      }

      // Add line for diagnosis tag
      if (item.is_diagnosis) {
        descLineCount += 1.3; // +0.3 for gap + 1 for tag
      }

      // Calculate rate/price column lines
      let ratePriceLines = 0;
      if (item.labor_hours && item.labor_hours > 0 && item.labor_rate) {
        ratePriceLines++; // Labor line
        const hasLaborDiscount = item.labor_discount_amount && item.labor_discount_amount > 0;
        if (hasLaborDiscount && options.showDiscountBreakdown) {
          ratePriceLines += 2; // Discount line + Final line
        }
      }
      if (item.parts && item.parts.length > 0) {
        ratePriceLines += 2; // Parts line + Total line
      }

      const baseRowHeight = 18;
      const descHeight = 5 + (descLineCount * 3.5);  // Start at 5, each line is 3.5mm
      const rateHeight = 6 + (ratePriceLines * 4);
      const rowHeight = Math.max(baseRowHeight, descHeight, rateHeight);

      // Alternating background
      if (index % 2 === 1) {
        pdf.setFillColor(250, 250, 250);
        pdf.rect(startX, yPosition, tableWidth, rowHeight, 'F');
      }

      // Borders
      if (options.borderStyle !== 'none') {
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.2);
        pdf.rect(startX, yPosition, tableWidth, rowHeight);

        let sepX = startX;
        colWidths.forEach((width, idx) => {
          if (idx < colWidths.length - 1) {
            sepX += width;
            pdf.line(sepX, yPosition, sepX, yPosition + rowHeight);
          }
        });
      }

      // Column 1: Description - with full 3C's format
      let descX = startX + 3;
      let descY = yPosition + 5;

      pdf.setFontSize(9);
      pdf.setTextColor(colors.text);
      pdf.setFont(undefined, 'bold');

      // Concern - show full text with wrapping
      const concernText = pdf.splitTextToSize(`Concern: ${item.concern}`, descWidth);
      concernText.forEach((line: string) => {
        pdf.text(line, descX, descY);
        descY += 3.5;
      });

      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(80, 80, 80);

      // Cause - show full text with wrapping
      if (item.cause) {
        descY += 1; // Small gap
        const causeLines = pdf.splitTextToSize(`Cause: ${item.cause}`, descWidth);
        causeLines.forEach((line: string) => {
          pdf.text(line, descX, descY);
          descY += 3.5;
        });
      }

      // Correction - show full text with wrapping
      if (item.correction) {
        descY += 1; // Small gap
        const correctionLines = pdf.splitTextToSize(`Correction: ${item.correction}`, descWidth);
        correctionLines.forEach((line: string) => {
          pdf.text(line, descX, descY);
          descY += 3.5;
        });
      }

      // Diagnosis tag
      if (item.is_diagnosis) {
        descY += 1;
        pdf.setFontSize(7);
        if (item.is_diagnosis_waived) {
          pdf.setTextColor(34, 197, 94);
          pdf.text('[DIAGNOSIS - WAIVED]', descX, descY);
        } else {
          pdf.setTextColor(59, 130, 246);
          pdf.text('[DIAGNOSIS]', descX, descY);
        }
      }

      // Column 2: Quantity
      pdf.setFontSize(fonts.body);
      pdf.setTextColor(colors.text);
      const qtyY = yPosition + (rowHeight / 2) + 2;
      pdf.text('1', startX + colWidths[0] + colWidths[1] / 2, qtyY, { align: 'center' });

      // Column 3: Rate/Price
      const rateX = startX + colWidths[0] + colWidths[1] + 3;
      let rateY = yPosition + 6;

      pdf.setFontSize(9);
      pdf.setTextColor(colors.text);

      // Labor
      if (item.labor_hours && item.labor_hours > 0 && item.labor_rate) {
        const hasLaborDiscount = item.labor_discount_amount && item.labor_discount_amount > 0;

        if (hasLaborDiscount && options.showDiscountBreakdown && item.labor_discount_amount) {
          const originalLaborCost = item.labor_hours * item.labor_rate;
          const discountPercent = item.labor_discount_percentage ||
            (item.labor_discount_amount / originalLaborCost * 100);

          pdf.text(`Labor: ${item.labor_hours}h @ ${this.formatCurrency(item.labor_rate)}/hr`, rateX, rateY);
          rateY += 3.5;

          pdf.setFontSize(8);
          pdf.setTextColor(220, 38, 38);  // RED to match estimate
          const discountText = discountPercent
            ? `  Discount: ${discountPercent.toFixed(2)}% (-${this.formatCurrency(item.labor_discount_amount)})`
            : `  Discount: -${this.formatCurrency(item.labor_discount_amount)}`;
          pdf.text(discountText, rateX, rateY);
          rateY += 3.5;

          pdf.setFontSize(9);
          pdf.setTextColor(34, 197, 94);  // GREEN to match estimate
          pdf.text(`  Final: ${this.formatCurrency(item.labor_total || 0)}`, rateX, rateY);
          pdf.setTextColor(colors.text);
        } else {
          pdf.text(`Labor: ${item.labor_hours}h @ ${this.formatCurrency(item.labor_rate)}/hr`, rateX, rateY);
        }
        rateY += 4;
      }

      // Parts
      if (item.parts && item.parts.length > 0) {
        const partsTotal = item.parts.reduce((sum, part) => sum + (part.quantity * part.unit_price), 0);

        pdf.setFontSize(9);
        pdf.setTextColor(colors.text);
        pdf.setFont(undefined, 'bold');
        pdf.text(`Parts: ${item.parts.length} item${item.parts.length !== 1 ? 's' : ''}`, rateX, rateY);
        pdf.setFont(undefined, 'normal');
        rateY += 4;

        pdf.setFontSize(9);
        pdf.setTextColor(0, 100, 0);
        pdf.text(`  Total: ${this.formatCurrency(item.parts_total || 0)}`, rateX, rateY);
        pdf.setTextColor(colors.text);
      }

      // Column 4: Amount
      const amountX = startX + colWidths[0] + colWidths[1] + colWidths[2];
      let amountY = yPosition + 6;

      pdf.setFontSize(fonts.body);
      pdf.setFont(undefined, 'bold');

      if (item.is_diagnosis_waived && item.original_line_total) {
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        const originalText = this.formatCurrency(item.original_line_total);
        pdf.text(originalText, amountX + colWidths[3] - 3, amountY, { align: 'right' });

        const textWidth = pdf.getTextWidth(originalText);
        pdf.setDrawColor(150, 150, 150);
        pdf.setLineWidth(0.3);
        pdf.line(amountX + colWidths[3] - 3 - textWidth, amountY - 1, amountX + colWidths[3] - 3, amountY - 1);

        amountY += 4;
        pdf.setFontSize(fonts.body);
        pdf.setTextColor(0, 150, 0);
        pdf.text(this.formatCurrency(item.line_total), amountX + colWidths[3] - 3, amountY, { align: 'right' });

        amountY += 4;
        pdf.setFontSize(7);
        pdf.setTextColor(200, 0, 0);
        const savings = item.original_line_total - item.line_total;
        pdf.text(`You save ${this.formatCurrency(savings)}`, amountX + colWidths[3] - 3, amountY, { align: 'right' });
      } else {
        pdf.setTextColor(colors.text);
        const simpleAmountY = yPosition + (rowHeight / 2) + 2;
        pdf.text(this.formatCurrency(item.line_total), amountX + colWidths[3] - 3, simpleAmountY, { align: 'right' });
      }

      pdf.setFont(undefined, 'normal');
      yPosition += rowHeight;

      // Parts breakdown table - always show when parts exist
      if (item.parts && item.parts.length > 0) {
        yPosition += 8;  // Increased spacing from 5

        pdf.setFontSize(9);  // Increased from 8
        pdf.setTextColor(100, 100, 100);
        pdf.setFont(undefined, 'bold');
        pdf.text('Parts Breakdown:', startX + 5, yPosition);
        yPosition += 5;  // Increased from 3

        // Check if any parts have discounts
        const hasPartDiscounts = item.parts.some(p => p.discount_percentage || p.discount_amount);

        const partsTableHeaders = hasPartDiscounts
          ? ['Part #', 'Description', 'Qty', 'Unit Price', 'Discount', 'Total']
          : ['Part #', 'Description', 'Qty', 'Unit Price', 'Total'];
        const partsColWidths = hasPartDiscounts
          ? [20, 40, 12, 20, 18, 20]
          : [25, 50, 15, 25, 25];
        const partsTableWidth = partsColWidths.reduce((sum, width) => sum + width, 0);
        const partsStartX = startX + 8;

        pdf.setFillColor(240, 240, 240);
        pdf.rect(partsStartX, yPosition, partsTableWidth, 6, 'F');

        if (options.borderStyle !== 'none') {
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.rect(partsStartX, yPosition, partsTableWidth, 6);
        }

        pdf.setFontSize(7);
        pdf.setTextColor(60, 60, 60);

        let partsXPos = partsStartX + 1;
        partsTableHeaders.forEach((header, pIndex) => {
          const textAlign = pIndex === 0 ? 'left' : pIndex >= 3 ? 'right' : 'center';
          const textX = textAlign === 'right' ? partsXPos + partsColWidths[pIndex] - 1 :
            textAlign === 'center' ? partsXPos + partsColWidths[pIndex] / 2 : partsXPos;

          pdf.text(header, textX, yPosition + 4, { align: textAlign });
          partsXPos += partsColWidths[pIndex];
        });

        yPosition += 6;
        pdf.setFont(undefined, 'normal');

        // Parts rows
        item.parts.forEach((part, partIndex) => {
          const partRowHeight = 5;

          if (partIndex % 2 === 1) {
            pdf.setFillColor(248, 248, 248);
            pdf.rect(partsStartX, yPosition, partsTableWidth, partRowHeight, 'F');
          }

          if (options.borderStyle !== 'none') {
            pdf.setDrawColor(220, 220, 220);
            pdf.setLineWidth(0.1);
            pdf.rect(partsStartX, yPosition, partsTableWidth, partRowHeight);

            let sepX = partsStartX;
            partsColWidths.forEach((width, idx) => {
              if (idx < partsColWidths.length - 1) {
                sepX += width;
                pdf.line(sepX, yPosition, sepX, yPosition + partRowHeight);
              }
            });
          }

          pdf.setFontSize(7);
          pdf.setTextColor(40, 40, 40);

          let partXPos = partsStartX + 1;

          pdf.text(part.part_number || 'N/A', partXPos, yPosition + 3);
          partXPos += partsColWidths[0];

          const maxDescLength = hasPartDiscounts ? 20 : 25;
          const partDesc = part.part_name.length > maxDescLength ?
            part.part_name.substring(0, maxDescLength) + '...' : part.part_name;
          pdf.text(partDesc, partXPos, yPosition + 3);
          partXPos += partsColWidths[1];

          pdf.text(part.quantity.toString(), partXPos + partsColWidths[2] / 2, yPosition + 3, { align: 'center' });
          partXPos += partsColWidths[2];

          pdf.text(this.formatCurrency(part.unit_price), partXPos + partsColWidths[3] - 1, yPosition + 3, { align: 'right' });
          partXPos += partsColWidths[3];

          if (hasPartDiscounts) {
            const discountText = part.discount_percentage
              ? `${part.discount_percentage.toFixed(2)}%`
              : part.discount_amount
                ? `-${this.formatCurrency(part.discount_amount)}`
                : '-';
            pdf.text(discountText, partXPos + partsColWidths[4] - 1, yPosition + 3, { align: 'right' });
            partXPos += partsColWidths[4];
          }

          pdf.setFont(undefined, 'bold');
          const totalColIndex = hasPartDiscounts ? 5 : 4;
          pdf.text(this.formatCurrency(part.total_price), partXPos + partsColWidths[totalColIndex] - 1, yPosition + 3, { align: 'right' });
          pdf.setFont(undefined, 'normal');

          yPosition += partRowHeight;
        });

        if (options.borderStyle !== 'none') {
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.line(partsStartX, yPosition, partsStartX + partsTableWidth, yPosition);
        }

        yPosition += 8;  // Increased from 5 for better spacing
      }
    });

    // Close table
    if (options.borderStyle !== 'none') {
      pdf.setDrawColor(colors.primary);
      pdf.setLineWidth(0.5);
      pdf.line(startX, yPosition, startX + tableWidth, yPosition);
    }

    return yPosition + 15;
  }

  private static addTotalSection(
    pdf: jsPDF,
    invoice: Invoice,
    colors: any,
    yPosition: number,
    pageWidth: number
  ): number {
    const rightAlign = pageWidth - 20;
    const labelX = rightAlign - 60;
    const valueX = rightAlign - 20;

    // Calculate total discounts/savings
    const totalSavings = invoice.line_items?.reduce((sum, item) => {
      let itemSavings = 0;
      if (item.labor_discount_amount) itemSavings += item.labor_discount_amount;
      if (item.is_diagnosis_waived && item.original_line_total) {
        itemSavings += item.original_line_total - item.line_total;
      }
      return sum + itemSavings;
    }, 0) || 0;

    // Calculate labor and parts totals from line items to show breakdown
    const laborTotal = invoice.line_items?.reduce((sum, item) => sum + (item.labor_total || 0), 0) || 0;
    const partsTotal = invoice.line_items?.reduce((sum, item) => sum + (item.parts_total || 0), 0) || 0;

    yPosition = this.checkPageBreak(pdf, yPosition, 60);

    // Show Labor and Parts breakdown first
    pdf.setFontSize(10);
    pdf.setTextColor(colors.text);

    // Labor Total
    pdf.text('Labor:', labelX, yPosition, { align: 'right' });
    pdf.text(this.formatCurrency(laborTotal), valueX, yPosition, { align: 'right' });
    yPosition += 6;

    // Parts Total
    pdf.text('Parts:', labelX, yPosition, { align: 'right' });
    pdf.text(this.formatCurrency(partsTotal), valueX, yPosition, { align: 'right' });
    yPosition += 6;

    // Show original subtotal if there are savings
    if (totalSavings > 0) {
      const originalSubtotal = (invoice.subtotal || 0) + totalSavings;

      pdf.text('Original Subtotal:', labelX, yPosition, { align: 'right' });
      pdf.text(this.formatCurrency(originalSubtotal), valueX, yPosition, { align: 'right' });
      yPosition += 6;

      pdf.setTextColor(220, 38, 38);
      pdf.text('Total Discounts:', labelX, yPosition, { align: 'right' });
      pdf.text(`-${this.formatCurrency(totalSavings).substring(1)}`, valueX, yPosition, { align: 'right' });
      yPosition += 6;
    }

    // Subtotal
    pdf.setFontSize(10);
    pdf.setTextColor(colors.text);
    pdf.text('Subtotal:', labelX, yPosition, { align: 'right' });
    pdf.text(this.formatCurrency(invoice.subtotal || 0), valueX, yPosition, { align: 'right' });
    yPosition += 6;

    // Tax
    pdf.text(`Tax (${this.formatPercentage(invoice.tax_rate || 0)}):`, labelX, yPosition, { align: 'right' });
    pdf.text(this.formatCurrency(invoice.tax_amount || 0), valueX, yPosition, { align: 'right' });
    yPosition += 6;

    pdf.setDrawColor(colors.text);
    pdf.setLineWidth(0.5);
    pdf.line(labelX - 5, yPosition, valueX + 5, yPosition);
    yPosition += 8;

    // Total
    pdf.setFontSize(14);
    pdf.setTextColor(colors.primary);
    pdf.text('TOTAL:', labelX, yPosition, { align: 'right' });
    pdf.text(this.formatCurrency(invoice.total_amount || 0), valueX, yPosition, { align: 'right' });
    yPosition += 8;

    // Amount Paid
    pdf.setFontSize(14);
    pdf.setTextColor(34, 197, 94);
    pdf.text('AMOUNT PAID:', labelX, yPosition, { align: 'right' });
    pdf.text(this.formatCurrency(invoice.amount_paid || 0), valueX, yPosition, { align: 'right' });
    yPosition += 12;

    // Paid in full box - wider to accommodate text
    const boxWidth = 110;  // Fixed width to ensure text fits
    const boxX = pageWidth - boxWidth - 20;  // Align to right side
    const boxHeight = 14;

    pdf.setFillColor(240, 253, 244);
    pdf.setDrawColor(34, 197, 94);
    pdf.setLineWidth(1);
    pdf.rect(boxX, yPosition - 6, boxWidth, boxHeight, 'FD');

    pdf.setFontSize(11);  // Slightly reduced from 12
    pdf.setTextColor(34, 197, 94);
    pdf.setFont(undefined, 'bold');
    pdf.text('PAID IN FULL - Balance: $0.00', boxX + boxWidth / 2, yPosition + 2, { align: 'center' });
    pdf.setFont(undefined, 'normal');

    // Savings summary
    if (totalSavings > 0) {
      yPosition += 14;
      pdf.setFontSize(10);
      pdf.setTextColor(16, 185, 129);
      pdf.text(`You saved ${this.formatCurrency(totalSavings)}!`, valueX, yPosition, { align: 'right' });
    }

    return yPosition + 20;
  }

  private static addNotes(
    pdf: jsPDF,
    invoice: Invoice,
    colors: any,
    yPosition: number,
    pageWidth: number
  ): number {
    yPosition = this.checkPageBreak(pdf, yPosition, 30);

    yPosition += 10;

    pdf.setFontSize(12);
    pdf.setTextColor(colors.primary);
    pdf.text('NOTES', 20, yPosition);
    yPosition += 8;

    pdf.setFontSize(9);
    pdf.setTextColor(colors.text);
    const notes = pdf.splitTextToSize(invoice.notes || '', pageWidth - 40);
    pdf.text(notes, 20, yPosition);

    return yPosition + (notes.length * 4) + 15;
  }

  private static addSignatureSection(
    pdf: jsPDF,
    invoice: Invoice,
    colors: any,
    yPosition: number,
    pageWidth: number
  ): number {
    yPosition = this.checkPageBreak(pdf, yPosition, 60);

    yPosition += 10;

    // Section title
    pdf.setFontSize(12);
    pdf.setTextColor(colors.primary);
    pdf.text('CUSTOMER AUTHORIZATION', 20, yPosition);
    yPosition += 10;

    // Authorization text
    pdf.setFontSize(8);
    pdf.setTextColor(100, 100, 100);
    const authText = 'I hereby authorize the repairs and services described above and agree to the charges.';
    pdf.text(authText, 20, yPosition);
    yPosition += 10;

    // Signature image
    const signatureData = (invoice as any).signature_data;
    if (signatureData) {
      try {
        pdf.addImage(signatureData, 'PNG', 25, yPosition, 60, 20);
      } catch (e) {
        console.warn('Failed to add signature image to invoice PDF', e);
      }
    }

    // Signature line
    const lineY = yPosition + 22;
    pdf.setDrawColor(150, 150, 150);
    pdf.setLineWidth(0.3);
    pdf.line(25, lineY, 115, lineY);

    pdf.setFontSize(8);
    pdf.setTextColor(colors.text);
    pdf.text('Customer Signature', 25, lineY + 5);

    // Date
    const signedAt = (invoice as any).signed_at;
    if (signedAt) {
      const dateStr = new Date(signedAt).toLocaleString();
      pdf.text(`Date: ${dateStr}`, 125, lineY);
    }

    return lineY + 15;
  }

  private static addFooter(
    pdf: jsPDF,
    companyInfo: CompanyInfo,
    colors: any,
    showPageNumbers: boolean = true
  ): void {
    const pageHeight = pdf.internal.pageSize.getHeight();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const totalPages = pdf.internal.getNumberOfPages();

    pdf.setPage(totalPages);

    const footerY = pageHeight - 15;

    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.1);
    pdf.line(20, footerY - 5, pageWidth - 20, footerY - 5);

    pdf.setFontSize(8);
    pdf.setTextColor(colors.text);

    pdf.text(`${companyInfo.name} | ${companyInfo.phone} | ${companyInfo.email}`,
      20, footerY);

    if (showPageNumbers) {
      const pageText = totalPages > 1 ?
        `Generated on ${new Date().toLocaleDateString()} | Page ${totalPages} of ${totalPages}` :
        `Generated on ${new Date().toLocaleDateString()}`;

      pdf.text(pageText, pageWidth - 20, footerY, { align: 'right' });
    }
  }
}
